"use client";

import type { Route } from "@/infrastructure/router/types";
import { useRouter } from "@/infrastructure/router/navigation";
import { type ReactNode, useEffect, useState } from "react";
import { useArtistSession } from "@/components/auth/ArtistSessionProvider";
import { useArtistRuntimeConfiguration } from "@/components/runtime/ArtistRuntimeConfigurationProvider";
import {
	ArtistIcon,
	type ArtistIconName,
	GlassButton,
	GlassCard,
	GlassSwitch,
	ListRow,
	NavigationBar,
	useDSAppearance,
} from "@/components/design-system/ArtistDesignSystem";
import {
	type DSAppearanceMode as AppearanceMode,
	DSAppearanceMode,
	DSAppearanceModeAll,
} from "@/components/design-system/foundations/appearance";
import {
	type ArtistLocale,
	ArtistSelectableLocaleAll,
	type TranslationKey,
	useTranslation,
} from "@/i18n/ArtistI18n";
import {
	AnalyticsAndImprovementsEnabledKey,
	QuickFeedbackEnabledKey,
	openFeedbackComposer,
	readBooleanPreference,
	writeBooleanPreference,
} from "@/domain/feedback/AppExperiencePreferences";

/**
 * SettingsHub — Web port of iOS `SettingsView.swift`.
 *
 * Five sections (account / general / make / about / sign-out) mirror the iOS
 * golden source. Appearance + language are no longer local-only preferences:
 * they delegate to `useDSAppearance()` (drives `data-theme` on `<html>`) and
 * `useTranslation().setLocale` (drives the i18n dictionary) so a tap on the
 * picker actually switches theme and language in-place.
 *
 * All visible strings resolve through `t(key)` — no hardcoded zh-Hans literals.
 */
const PREFS_KEY = "artist.preferences.settings.v2";

type FeedType = "default" | "forYou";
type MakeWorkTab = "studio" | "feed";
type DisplayFilter = "allWorks" | "pinned";
type VideoSound = "allowSound" | "muteSound";
type AudioQuality = "auto" | "high" | "lossless" | "hiRes";

interface SettingsPreferences {
	feedType: FeedType;
	makeWorkTab: MakeWorkTab;
	displayFilter: DisplayFilter;
	videoSound: VideoSound;
	audioQuality: AudioQuality;
	paidGenerateConfirm: boolean;
	quickFeedbackEnabled: boolean;
	analyticsAndImprovementsEnabled: boolean;
}

interface SettingsActionRowProps {
	readonly icon: ArtistIconName;
	readonly title: string;
	readonly onClick: () => void;
}

function SettingsActionRow({
	icon,
	title,
	onClick,
}: Readonly<SettingsActionRowProps>) {
	return (
		<button type="button" className="settings-row settings-row--action" onClick={onClick}>
			<span className="settings-row__leading" aria-hidden="true">
				<ArtistIcon name={icon} />
			</span>
			<span className="settings-row__copy">
				<strong>{title}</strong>
			</span>
			<span className="settings-row__trailing">
				<ArtistIcon
					name="chevron"
					size={14}
					className="settings-row__trailing-chevron"
				/>
			</span>
		</button>
	);
}

const DEFAULT_PREFS: SettingsPreferences = {
	feedType: "default",
	makeWorkTab: "studio",
	displayFilter: "allWorks",
	videoSound: "allowSound",
	audioQuality: "auto",
	paidGenerateConfirm: true,
	quickFeedbackEnabled: true,
	analyticsAndImprovementsEnabled: true,
};

interface PickerOption {
	readonly value: string;
	readonly labelKey: TranslationKey;
}

const FEED_TYPE_OPTIONS: ReadonlyArray<PickerOption> = [
	{ value: "default", labelKey: "settings.feedType.default" },
	{ value: "forYou", labelKey: "settings.feedType.forYou" },
];

const MAKE_WORK_TAB_OPTIONS: ReadonlyArray<PickerOption> = [
	{ value: "studio", labelKey: "settings.makeWorkTab.studio" },
	{ value: "feed", labelKey: "settings.makeWorkTab.feed" },
];

const DISPLAY_FILTER_OPTIONS: ReadonlyArray<PickerOption> = [
	{ value: "allWorks", labelKey: "settings.displayFilter.allWorks" },
	{ value: "pinned", labelKey: "settings.displayFilter.pinned" },
];

const VIDEO_SOUND_OPTIONS: ReadonlyArray<PickerOption> = [
	{ value: "allowSound", labelKey: "settings.videoSound.allowSound" },
	{ value: "muteSound", labelKey: "settings.videoSound.muteSound" },
];

const AUDIO_QUALITY_OPTIONS: ReadonlyArray<PickerOption> = [
	{ value: "auto", labelKey: "settings.audioQuality.auto" },
	{ value: "high", labelKey: "settings.audioQuality.high" },
	{ value: "lossless", labelKey: "settings.audioQuality.lossless" },
	{ value: "hiRes", labelKey: "settings.audioQuality.hiRes" },
];

const APPEARANCE_OPTIONS: ReadonlyArray<{
	value: AppearanceMode;
	labelKey: TranslationKey;
}> = DSAppearanceModeAll.map((mode) => {
	const labelKey =
		mode === DSAppearanceMode.system
			? "settings.appearance.system"
			: mode === DSAppearanceMode.light
				? "settings.appearance.light"
				: "settings.appearance.dark";
	return { value: mode, labelKey };
});

const LANGUAGE_OPTIONS: ReadonlyArray<{
	value: ArtistLocale;
	labelKey: TranslationKey;
}> = ArtistSelectableLocaleAll.map((locale) => {
	const labelKey =
		locale === "system"
			? "settings.language.system"
			: locale === "zh-Hans"
				? "settings.language.zh-Hans"
				: "settings.language.en";
	return { value: locale, labelKey };
});

function optionLabel(
	options: ReadonlyArray<PickerOption>,
	value: string,
	t: (key: TranslationKey) => string,
): string {
	const match = options.find((option) => option.value === value);
	return match ? t(match.labelKey) : "";
}

export function SettingsHub() {
	const router = useRouter();
	const auth = useArtistSession();
	const runtimeConfiguration = useArtistRuntimeConfiguration();
	const { t, locale, setLocale } = useTranslation();
	const appearance = useDSAppearance();

	const [prefs, setPrefs] = useState<SettingsPreferences>(DEFAULT_PREFS);
	const [ready, setReady] = useState(false);
	const [confirmSignOut, setConfirmSignOut] = useState(false);
	const [signingOut, setSigningOut] = useState(false);
	const isSignedIn = auth.phase === "user";

	useEffect(() => {
		try {
			const raw = window.localStorage.getItem(PREFS_KEY);
			if (raw !== null) {
				const candidate = JSON.parse(raw) as Partial<SettingsPreferences>;
				setPrefs({
					feedType: candidate.feedType ?? DEFAULT_PREFS.feedType,
					makeWorkTab: candidate.makeWorkTab ?? DEFAULT_PREFS.makeWorkTab,
					displayFilter: candidate.displayFilter ?? DEFAULT_PREFS.displayFilter,
					videoSound: candidate.videoSound ?? DEFAULT_PREFS.videoSound,
					audioQuality: candidate.audioQuality ?? DEFAULT_PREFS.audioQuality,
					paidGenerateConfirm:
						candidate.paidGenerateConfirm ?? DEFAULT_PREFS.paidGenerateConfirm,
					quickFeedbackEnabled:
						candidate.quickFeedbackEnabled ??
						readBooleanPreference(QuickFeedbackEnabledKey, true),
					analyticsAndImprovementsEnabled:
						candidate.analyticsAndImprovementsEnabled ??
						readBooleanPreference(
							AnalyticsAndImprovementsEnabledKey,
							true,
						),
				});
			} else {
				setPrefs((current) => ({
					...current,
					quickFeedbackEnabled: readBooleanPreference(
						QuickFeedbackEnabledKey,
						true,
					),
					analyticsAndImprovementsEnabled: readBooleanPreference(
						AnalyticsAndImprovementsEnabledKey,
						true,
					),
				}));
			}
		} finally {
			setReady(true);
		}
	}, []);

	function patch(partial: Partial<SettingsPreferences>): void {
		const next: SettingsPreferences = { ...prefs, ...partial };
		setPrefs(next);
		window.localStorage.setItem(PREFS_KEY, JSON.stringify(next));
	}

	function setQuickFeedbackEnabled(enabled: boolean): void {
		writeBooleanPreference(QuickFeedbackEnabledKey, enabled);
		patch({ quickFeedbackEnabled: enabled });
	}

	function setAnalyticsAndImprovementsEnabled(enabled: boolean): void {
		writeBooleanPreference(AnalyticsAndImprovementsEnabledKey, enabled);
		patch({ analyticsAndImprovementsEnabled: enabled });
	}

	async function signOut(): Promise<void> {
		setSigningOut(true);
		const complete = await auth.signOut();
		setSigningOut(false);
		setConfirmSignOut(false);
		if (complete) router.replace("/auth");
	}

	const accountEditHref = isSignedIn ? "/profile/edit" : "/auth";
	const accountEditTitle = isSignedIn
		? t("settings.account.editProfile")
		: t("settings.account.login");
	const connectedAccountsHref = isSignedIn ? "/account/connected" : "/auth";

	return (
		<section
			className="settings-hub native-page"
			aria-label={t("settings.title")}
			aria-busy={!ready || auth.phase === "loading"}
		>
			<NavigationBar title={t("settings.title")} backHref="/library" />

			<div className="settings-layout">
				<SettingsSection title={t("settings.section.account")}>
					<GlassCard className="settings-card" style={{ padding: 0 }}>
						<SettingsNavigationRow
							icon="person"
							title={accountEditTitle}
							href={accountEditHref}
						/>
						<SettingsDivider />
						<SettingsNavigationRow
							icon="crown"
							title={t("settings.account.membership")}
							href="/billing/membership"
						/>
						<SettingsDivider />
						<SettingsNavigationRow
							icon="shield"
							title={t("settings.account.connectedAccounts")}
							href={connectedAccountsHref}
						/>
						<SettingsDivider />
						<SettingsPickerRow
							icon="home"
							title={t("settings.account.homeFeed")}
							value={prefs.feedType}
							options={FEED_TYPE_OPTIONS}
							disabled={!ready}
							onSelect={(value) => patch({ feedType: value as FeedType })}
						/>
					</GlassCard>
				</SettingsSection>

				<SettingsSection title={t("settings.section.general")}>
					<GlassCard className="settings-card" style={{ padding: 0 }}>
						<SettingsAppearanceRow
							value={appearance.mode}
							disabled={!ready}
							onSelect={(mode) => appearance.setMode(mode)}
						/>
						<SettingsDivider />
						<SettingsLanguageRow
							value={locale}
							disabled={!ready}
							onSelect={(next) => setLocale(next)}
						/>
						<SettingsDivider />
						<SettingsNavigationRow
							icon="sliders"
							title={t("settings.general.player")}
							href="/settings/player"
						/>
						<SettingsDivider />
						<SettingsNavigationRow
							icon="waveform"
							title={t("settings.general.audioQuality")}
							href="/settings/audio-quality"
							trailingValue={optionLabel(
								AUDIO_QUALITY_OPTIONS,
								prefs.audioQuality,
								t,
							)}
						/>
						<SettingsDivider />
						<SettingsNavigationRow
							icon="shield"
							title={t("settings.general.permissions")}
							href="/settings/permissions"
						/>
						<SettingsDivider />
						<SettingsNavigationRow
							icon="image"
							title={t("settings.general.devicePlayback")}
							href="/settings/device-playback"
						/>
					</GlassCard>
				</SettingsSection>

				<SettingsSection title={t("settings.section.feedbackPrivacy")}>
					<GlassCard className="settings-card" style={{ padding: 0 }}>
						<SettingsActionRow
							icon="more"
							title={t("settings.feedback.open")}
							onClick={openFeedbackComposer}
						/>
						<SettingsDivider />
						<SettingsToggleRow
							icon="sparkle"
							title={t("settings.feedback.quickEntry")}
							checked={prefs.quickFeedbackEnabled}
							disabled={!ready}
							onChange={setQuickFeedbackEnabled}
						/>
						<SettingsDivider />
						<SettingsToggleRow
							icon="shield"
							title={t("settings.feedback.analytics")}
							checked={prefs.analyticsAndImprovementsEnabled}
							disabled={!ready}
							onChange={setAnalyticsAndImprovementsEnabled}
						/>
					</GlassCard>
				</SettingsSection>

				<SettingsSection title={t("settings.section.make")}>
					<GlassCard className="settings-card" style={{ padding: 0 }}>
						<SettingsPickerRow
							icon="create"
							title={t("settings.makeWorkTab.title")}
							value={prefs.makeWorkTab}
							options={MAKE_WORK_TAB_OPTIONS}
							disabled={!ready}
							onSelect={(value) => patch({ makeWorkTab: value as MakeWorkTab })}
						/>
						<SettingsDivider />
						<SettingsPickerRow
							icon="more"
							title={t("settings.displayFilter.title")}
							value={prefs.displayFilter}
							options={DISPLAY_FILTER_OPTIONS}
							disabled={!ready}
							onSelect={(value) =>
								patch({ displayFilter: value as DisplayFilter })
							}
						/>
						<SettingsDivider />
						<SettingsPickerRow
							icon="music"
							title={t("settings.videoSound.title")}
							value={prefs.videoSound}
							options={VIDEO_SOUND_OPTIONS}
							disabled={!ready}
							onSelect={(value) => patch({ videoSound: value as VideoSound })}
						/>
						<SettingsDivider />
						<SettingsToggleRow
							icon="check"
							title={t("settings.paidGenerateConfirm.title")}
							checked={prefs.paidGenerateConfirm}
							disabled={!ready}
							onChange={(checked) => patch({ paidGenerateConfirm: checked })}
						/>
					</GlassCard>
				</SettingsSection>

				<SettingsSection title={t("settings.section.about")}>
					<GlassCard className="settings-card" style={{ padding: 0 }}>
						<SettingsInfoRow
							icon="info"
							title={t("settings.about.artist")}
							href="https://artist.app/about"
						/>
						<SettingsDivider />
						<SettingsInfoRow
							icon="doc"
							title={t("settings.about.userAgreement")}
							href="/terms"
						/>
						<SettingsDivider />
						<SettingsInfoRow
							icon="check"
							title={t("settings.about.appleEULA")}
							href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/"
						/>
						<SettingsDivider />
						<SettingsInfoRow
							icon="shield"
							title={t("settings.about.privacyPolicy")}
							href="/privacy"
						/>
					</GlassCard>
				</SettingsSection>

				<section className="settings-signout">
					{isSignedIn ? (
						confirmSignOut ? (
							<GlassCard
								role="alertdialog"
								aria-labelledby="signout-title"
								className="settings-signout-confirm"
							>
								<strong id="signout-title">
									{t("settings.logout.confirmTitle")}
								</strong>
								<p>{t("settings.logout.confirmMessage")}</p>
								<div className="settings-signout-actions">
									<GlassButton
										kind="destructive"
										type="button"
										onClick={() => void signOut()}
										disabled={signingOut}
									>
										{signingOut
											? t("settings.logout.inProgress")
											: t("settings.logout.confirm")}
									</GlassButton>
									<GlassButton
										type="button"
										onClick={() => setConfirmSignOut(false)}
										disabled={signingOut}
									>
										{t("settings.logout.cancel")}
									</GlassButton>
								</div>
							</GlassCard>
						) : (
							<SettingsDestructiveButton
								label={t("settings.logout.action")}
								icon="back"
								loading={signingOut}
								onClick={() => setConfirmSignOut(true)}
							/>
						)
					) : (
						<SettingsDestructiveButton
							label={t("settings.login.action")}
							icon="person"
							kind="primary"
							onClick={() => router.push("/auth")}
						/>
					)}
				</section>

				{runtimeConfiguration.region === "china" ? (
					<footer className="settings-icp-footer">粤ICP备20044716号-2A</footer>
				) : null}
			</div>
		</section>
	);
}

function SettingsSection({
	title,
	children,
}: Readonly<{ title: string; children: ReactNode }>) {
	return (
		<section className="settings-section">
			<SettingsSectionHeader title={title} />
			{children}
		</section>
	);
}

function SettingsSectionHeader({ title }: Readonly<{ title: string }>) {
	return (
		<header>
			<h2>{title}</h2>
		</header>
	);
}

function SettingsDivider() {
	return <div className="settings-divider" aria-hidden="true" />;
}

interface SettingsNavigationRowProps {
	readonly icon: ArtistIconName;
	readonly title: string;
	readonly href: Route;
	readonly trailingValue?: string;
	readonly disabled?: boolean;
}

function SettingsNavigationRow({
	icon,
	title,
	href,
	trailingValue,
	disabled = false,
}: Readonly<SettingsNavigationRowProps>) {
	return (
		<ListRow
			href={href}
			title={title}
			leading={<ArtistIcon name={icon} />}
			trailing={
				<span className="settings-row__trailing">
					{trailingValue === undefined || trailingValue === "" ? null : (
						<span className="settings-row__trailing-value">
							{trailingValue}
						</span>
					)}
					<ArtistIcon
						name="chevron"
						size={14}
						className="settings-row__trailing-chevron"
					/>
				</span>
			}
			disabled={disabled}
		/>
	);
}

interface SettingsInfoRowProps {
	readonly icon: ArtistIconName;
	readonly title: string;
	readonly href: Route;
}

function SettingsInfoRow({
	icon,
	title,
	href,
}: Readonly<SettingsInfoRowProps>) {
	return (
		<ListRow
			href={href}
			title={title}
			leading={<ArtistIcon name={icon} />}
			trailing={
				<span className="settings-row__trailing">
					<ArtistIcon
						name="chevron"
						size={14}
						className="settings-row__trailing-chevron"
					/>
				</span>
			}
		/>
	);
}

interface SettingsPickerRowProps {
	readonly icon: ArtistIconName;
	readonly title: string;
	readonly value: string;
	readonly options: ReadonlyArray<PickerOption>;
	readonly disabled?: boolean;
	readonly onSelect: (value: string) => void;
}

function SettingsPickerRow({
	icon,
	title,
	value,
	options,
	disabled = false,
	onSelect,
}: Readonly<SettingsPickerRowProps>) {
	const { t } = useTranslation();
	const selected =
		options.find((option) => option.value === value) ?? options[0];
	const selectedValue = selected?.value ?? "";
	return (
		<label
			className={`settings-row settings-row--picker${disabled ? " settings-row--disabled" : ""}`}
		>
			<span className="settings-row__leading" aria-hidden="true">
				<ArtistIcon name={icon} />
			</span>
			<span className="settings-row__copy">
				<strong>{title}</strong>
			</span>
			<span className="settings-row__picker">
				<select
					value={selectedValue}
					disabled={disabled}
					onChange={(event) => onSelect(event.currentTarget.value)}
					aria-label={title}
				>
					{options.map((option) => (
						<option key={option.value} value={option.value}>
							{t(option.labelKey)}
						</option>
					))}
				</select>
				<ArtistIcon
					name="chevron"
					size={14}
					className="settings-row__picker-chevron"
				/>
			</span>
		</label>
	);
}

interface SettingsAppearanceRowProps {
	readonly value: AppearanceMode;
	readonly disabled?: boolean;
	readonly onSelect: (mode: AppearanceMode) => void;
}

function SettingsAppearanceRow({
	value,
	disabled = false,
	onSelect,
}: Readonly<SettingsAppearanceRowProps>) {
	const { t } = useTranslation();
	return (
		<label
			className={`settings-row settings-row--picker${disabled ? " settings-row--disabled" : ""}`}
		>
			<span className="settings-row__leading" aria-hidden="true">
				<ArtistIcon name="sparkle" />
			</span>
			<span className="settings-row__copy">
				<strong>{t("settings.general.appearance")}</strong>
			</span>
			<span className="settings-row__picker">
				<select
					value={value}
					disabled={disabled}
					onChange={(event) =>
						onSelect(event.currentTarget.value as AppearanceMode)
					}
					aria-label={t("settings.general.appearance")}
				>
					{APPEARANCE_OPTIONS.map((option) => (
						<option key={option.value} value={option.value}>
							{t(option.labelKey)}
						</option>
					))}
				</select>
				<ArtistIcon
					name="chevron"
					size={14}
					className="settings-row__picker-chevron"
				/>
			</span>
		</label>
	);
}

interface SettingsLanguageRowProps {
	readonly value: ArtistLocale;
	readonly disabled?: boolean;
	readonly onSelect: (locale: ArtistLocale) => void;
}

function SettingsLanguageRow({
	value,
	disabled = false,
	onSelect,
}: Readonly<SettingsLanguageRowProps>) {
	const { t } = useTranslation();
	return (
		<label
			className={`settings-row settings-row--picker${disabled ? " settings-row--disabled" : ""}`}
		>
			<span className="settings-row__leading" aria-hidden="true">
				<ArtistIcon name="globe" />
			</span>
			<span className="settings-row__copy">
				<strong>{t("settings.general.language")}</strong>
			</span>
			<span className="settings-row__picker">
				<select
					value={value}
					disabled={disabled}
					onChange={(event) =>
						onSelect(event.currentTarget.value as ArtistLocale)
					}
					aria-label={t("settings.general.language")}
				>
					{LANGUAGE_OPTIONS.map((option) => (
						<option key={option.value} value={option.value}>
							{t(option.labelKey)}
						</option>
					))}
				</select>
				<ArtistIcon
					name="chevron"
					size={14}
					className="settings-row__picker-chevron"
				/>
			</span>
		</label>
	);
}

interface SettingsToggleRowProps {
	readonly icon: ArtistIconName;
	readonly title: string;
	readonly checked: boolean;
	readonly disabled?: boolean;
	readonly onChange: (checked: boolean) => void;
}

function SettingsToggleRow({
	icon,
	title,
	checked,
	disabled = false,
	onChange,
}: Readonly<SettingsToggleRowProps>) {
	return (
		<div
			className={`settings-row settings-row--toggle${disabled ? " settings-row--disabled" : ""}`}
		>
			<span className="settings-row__leading" aria-hidden="true">
				<ArtistIcon name={icon} />
			</span>
			<span className="settings-row__copy">
				<strong>{title}</strong>
			</span>
			<GlassSwitch
				checked={checked}
				disabled={disabled}
				onChange={onChange}
				label={title}
			/>
		</div>
	);
}

interface SettingsDestructiveButtonProps {
	readonly label: string;
	readonly icon: ArtistIconName;
	readonly kind?: "primary" | "destructive";
	readonly loading?: boolean;
	readonly onClick: () => void;
}

function SettingsDestructiveButton({
	label,
	icon,
	kind = "destructive",
	loading = false,
	onClick,
}: Readonly<SettingsDestructiveButtonProps>) {
	const { t } = useTranslation();
	return (
		<GlassButton
			kind={kind}
			type="button"
			expands
			isEnabled={!loading}
			symbol={<ArtistIcon name={icon} />}
			onClick={onClick}
		>
			{loading ? t("settings.logout.inProgress") : label}
		</GlassButton>
	);
}
